import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { loadGoogleMaps } from "@/lib/gmaps";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Save, MapPin } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin page for drawing the service area on a Google Map. Supports multiple
 * circles (e.g. main service town + a satellite town / rural spur).
 *
 *  • Click anywhere on the map to drop a new circle at that spot.
 *  • Drag the center marker to move it.
 *  • Drag any edge handle to resize the radius.
 *  • Use the sidebar list to name each area, edit the radius numerically,
 *    toggle active/inactive, or delete.
 *  • Hit Save to persist.
 *
 * All customer signups from the WordPress widget are then geocoded and
 * matched against these circles. Out-of-area signups still land in the
 * lead pipeline, but tagged ``waitlist`` for a separate review flow.
 */
const DEFAULT_CENTER = { lat: 30.8419, lng: -97.7973 };  // Florence, TX

export default function ServiceArea() {
    const mapContainer = useRef(null);
    const mapRef = useRef(null);
    const circlesRef = useRef(new Map());
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [mapError, setMapError] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/settings/service-areas");
            setAreas(data.service_areas || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load service areas");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Build the map once
    useEffect(() => {
        let disposed = false;
        // Detect Google Maps billing/key errors that the JS API renders via
        // console.error rather than reject-ing the promise (frustratingly).
        const origErr = console.error;
        console.error = (...args) => {
            const msg = args.map((a) => String(a)).join(" ");
            if (/BillingNotEnabledMapError|InvalidKeyMapError|ApiNotActivatedMapError|RefererNotAllowedMapError/i.test(msg)) {
                if (!disposed) setMapError(msg.match(/[A-Z][a-zA-Z]+MapError/)?.[0] || "GoogleMapsError");
            }
            origErr.apply(console, args);
        };
        loadGoogleMaps().then((google) => {
            if (disposed || !mapContainer.current) return;
            mapRef.current = new google.maps.Map(mapContainer.current, {
                center: DEFAULT_CENTER,
                zoom: 10,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            });
            mapRef.current.addListener("click", (e) => {
                addArea({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            });
        }).catch((err) => {
            if (!disposed) setMapError(err.message || "Map failed to load");
        });
        return () => {
            disposed = true;
            console.error = origErr;
        };
    }, []);

    // Sync circles to state
    useEffect(() => {
        if (!mapRef.current || !window.google) return;
        const google = window.google;
        // Remove circles no longer in state
        for (const [id, entry] of circlesRef.current) {
            if (!areas.find((a) => a.id === id)) {
                entry.circle.setMap(null);
                entry.marker.setMap(null);
                circlesRef.current.delete(id);
            }
        }
        // Add / update
        for (const area of areas) {
            const existing = circlesRef.current.get(area.id);
            if (existing) {
                existing.circle.setCenter(area.center);
                existing.circle.setRadius(area.radius_miles * 1609.344);
                existing.circle.setOptions({
                    fillOpacity: area.active === false ? 0.05 : 0.15,
                    strokeOpacity: area.active === false ? 0.3 : 0.8,
                });
                existing.marker.setPosition(area.center);
                existing.marker.setLabel(area.name?.charAt(0)?.toUpperCase() || "•");
                continue;
            }
            const circle = new google.maps.Circle({
                map: mapRef.current,
                center: area.center,
                radius: area.radius_miles * 1609.344,
                fillColor: "#3b82f6",
                fillOpacity: area.active === false ? 0.05 : 0.15,
                strokeColor: "#1d4ed8",
                strokeOpacity: area.active === false ? 0.3 : 0.8,
                strokeWeight: 2,
                editable: true,
                draggable: false,
            });
            const marker = new google.maps.Marker({
                map: mapRef.current,
                position: area.center,
                draggable: true,
                label: {
                    text: area.name?.charAt(0)?.toUpperCase() || "•",
                    color: "#ffffff",
                    fontWeight: "700",
                },
            });
            circle.addListener("radius_changed", () => {
                const meters = circle.getRadius();
                patchArea(area.id, { radius_miles: +(meters / 1609.344).toFixed(2) });
            });
            circle.addListener("center_changed", () => {
                const c = circle.getCenter();
                marker.setPosition(c);
                patchArea(area.id, { center: { lat: c.lat(), lng: c.lng() } });
            });
            marker.addListener("dragend", () => {
                const p = marker.getPosition();
                circle.setCenter(p);
                patchArea(area.id, { center: { lat: p.lat(), lng: p.lng() } });
            });
            circlesRef.current.set(area.id, { circle, marker });
        }
    }, [areas]);

    // Fit map to circles when they change from a load
    useEffect(() => {
        if (!mapRef.current || !window.google || areas.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        areas.forEach((a) => {
            const r = a.radius_miles * 1609.344;
            const c = new window.google.maps.LatLng(a.center.lat, a.center.lng);
            const circle = new window.google.maps.Circle({ center: c, radius: r });
            bounds.union(circle.getBounds());
        });
        if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds);
    }, [loading]);  // once after initial load

    const addArea = (center) => {
        const newArea = {
            id: `tmp-${Date.now()}`,
            name: `Area ${areas.length + 1}`,
            center,
            radius_miles: 5,
            active: true,
        };
        setAreas((cur) => [...cur, newArea]);
        setDirty(true);
    };

    const patchArea = (id, patch) => {
        setAreas((cur) => cur.map((a) => (a.id === id ? { ...a, ...patch } : a)));
        setDirty(true);
    };

    const removeArea = (id) => {
        setAreas((cur) => cur.filter((a) => a.id !== id));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/settings/service-areas", {
                service_areas: areas.map(({ id, ...rest }) => ({
                    id: id.startsWith("tmp-") ? undefined : id, ...rest,
                })),
            });
            setAreas(data.service_areas);
            setDirty(false);
            toast.success("Service areas saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-6" data-testid="service-area-page">
            <PageHeader
                title="Service area"
                subtitle="Draw circles on the map to define where you'll pick up. Website signups outside these areas go to a waitlist for office review."
                actions={
                    <Button size="sm" onClick={save} disabled={!dirty || saving}
                            data-testid="service-area-save">
                        <Save className="w-4 h-4 mr-1" />
                        {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                    </Button>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2 overflow-hidden">
                    {mapError ? (
                        <div className="w-full h-[70vh] flex items-center justify-center p-8 bg-secondary/30">
                            <div className="max-w-md text-center space-y-3">
                                <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
                                    <MapPin className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div className="font-semibold">Google Maps can't load</div>
                                <div className="text-sm text-muted-foreground">
                                    {mapError.includes("BillingNotEnabled")
                                        ? "Billing is not enabled on your Google Cloud project. Turn it on in Google Cloud Console → Billing to unlock the map."
                                        : mapError.includes("RefererNotAllowed")
                                        ? "The Google Maps API key is restricted to a different domain. Add this site's URL to the key's HTTP referrer allowlist in Google Cloud Console."
                                        : mapError.includes("ApiNotActivated")
                                        ? "Enable the Maps JavaScript API for your key in Google Cloud Console."
                                        : mapError.includes("InvalidKey")
                                        ? "The REACT_APP_GOOGLE_MAPS_KEY value looks invalid."
                                        : "Check the Google Maps API key and its restrictions."}
                                </div>
                                <a href="https://console.cloud.google.com/google/maps-apis/overview"
                                   target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                    Open Google Cloud Console →
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div ref={mapContainer} data-testid="service-area-map"
                             className="w-full h-[70vh] bg-secondary/30" />
                    )}
                </Card>

                <Card className="lg:col-span-1">
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="overline">Areas · {areas.length}</div>
                            <Button size="sm" variant="outline"
                                    onClick={() => addArea(mapRef.current?.getCenter()
                                        ? { lat: mapRef.current.getCenter().lat(), lng: mapRef.current.getCenter().lng() }
                                        : DEFAULT_CENTER)}
                                    data-testid="service-area-add">
                                <Plus className="w-3.5 h-3.5 mr-1" />Add
                            </Button>
                        </div>

                        {loading && (
                            <div className="text-xs text-muted-foreground py-6 text-center">
                                Loading…
                            </div>
                        )}

                        {!loading && areas.length === 0 && (
                            <div className="text-xs text-muted-foreground border border-dashed border-border rounded p-4 text-center">
                                <MapPin className="w-6 h-6 mx-auto mb-1 opacity-40" />
                                Click anywhere on the map to place your first service circle.
                            </div>
                        )}

                        <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
                            {areas.map((a) => (
                                <div key={a.id}
                                     className={`p-3 rounded-md border ${a.active === false ? "border-border/40 bg-muted/30" : "border-border surface"}`}
                                     data-testid={`service-area-item-${a.id}`}>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={a.name}
                                            onChange={(e) => patchArea(a.id, { name: e.target.value })}
                                            className="h-8 text-sm font-medium"
                                            data-testid={`service-area-name-${a.id}`}
                                        />
                                        <Button size="icon" variant="ghost"
                                                onClick={() => removeArea(a.id)}
                                                className="h-8 w-8 shrink-0"
                                                data-testid={`service-area-remove-${a.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <div>
                                            <Label className="text-[10px]">Radius (miles)</Label>
                                            <Input type="number" min="0.1" step="0.5"
                                                   value={a.radius_miles}
                                                   onChange={(e) => patchArea(a.id, { radius_miles: parseFloat(e.target.value) || 0 })}
                                                   className="h-7 text-xs"
                                                   data-testid={`service-area-radius-${a.id}`}/>
                                        </div>
                                        <div>
                                            <Label className="text-[10px]">Status</Label>
                                            <Button
                                                size="sm" variant="outline"
                                                className="h-7 w-full text-xs"
                                                onClick={() => patchArea(a.id, { active: a.active === false })}
                                                data-testid={`service-area-active-${a.id}`}>
                                                {a.active === false ? "Inactive" : "Active"}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-[10px] font-mono text-muted-foreground mt-2 truncate">
                                        {a.center.lat.toFixed(4)}, {a.center.lng.toFixed(4)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
